import { useCallback, useEffect, useRef, useState } from 'react';
import { api } from '../../../../services/api';
import { atfStatusInfo, periodoParaDatas, statusDoFiltro } from './atfStatusInfo';
import type { AtfItem, HistoricoAtfResponse, MotivoCancelamento } from './types';

const LIMIT = 30;

/**
 * Polling 10s/60s, teto de 10 rodadas — research.md §1.4 da 0034 (varredura do
 * widget de acompanhamento do app). 10s enquanto houver item `emAndamento`
 * (PENDENTE/PROCESSANDO); 60s quando só sobrarem itens `revalidar` (hoje só
 * ERRO). O teto fica exposto para a tela avisar, porque o painel não precisa
 * herdar o silêncio do app.
 */
const INTERVALO_RAPIDO_MS = 10_000;
const INTERVALO_LENTO_MS = 60_000;
const TETO_RODADAS = 10;

export interface FiltrosAcompanhamento {
  status: string;
  periodo: string;
  busca: string;
}

export function useAcompanhamento() {
  const [itens, setItens] = useState<AtfItem[]>([]);
  const [total, setTotal] = useState(0);
  const [carregando, setCarregando] = useState(true);
  const [atualizando, setAtualizando] = useState(false);
  const [atualizadoEm, setAtualizadoEm] = useState<Date | null>(null);
  const [erro, setErro] = useState<string | null>(null);
  const [filtros, setFiltros] = useState<FiltrosAcompanhamento>({
    status: 'TODOS', periodo: '30', busca: '',
  });
  const [tetoAtingido, setTetoAtingido] = useState(false);
  const [rodada, setRodada] = useState(0);

  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const rodadasRef = useRef(0);
  /**
   * Os dois sinais que decidem se e quando repetir.
   *
   * Ficam em ref, não em estado, de propósito: quando `itens` entrava na
   * dependência do agendamento, cada `setItens` gerava uma referência nova, o
   * efeito limpava o timer e reagendava — e como a contagem de rodadas só subia
   * DENTRO do setTimeout, o teto podia nunca ser alcançado e o intervalo real
   * divergia dos 10s/60s pretendidos.
   */
  const sinaisRef = useRef({ emAndamento: false, revalidar: false });
  const filtrosRef = useRef(filtros);
  filtrosRef.current = filtros;
  /**
   * Versão da busca em curso.
   *
   * Sem ela, trocar de filtro enquanto uma resposta lenta está no ar deixa a
   * antiga chegar por último e sobrescrever a nova — a tela mostra o resultado
   * de um filtro que o cooperado já abandonou, e `sinaisRef` passa a decidir o
   * polling a partir dessa página velha.
   */
  const versaoRef = useRef(0);

  const buscar = useCallback(async (f: FiltrosAcompanhamento, { manual = false } = {}) => {
    const versao = ++versaoRef.current;
    if (manual) {
      rodadasRef.current = 0;
      setRodada(0);
      setTetoAtingido(false);
      setAtualizando(true);
    }
    setErro(null);
    try {
      const status = statusDoFiltro(f.status);
      const { dataInicio, dataFim } = periodoParaDatas(f.periodo);
      const params: Record<string, string | number> = { limit: LIMIT, offset: 0 };
      // O servidor aceita um status por vez; "Em andamento" cobre dois, então
      // esse caso vai sem recorte e é fechado no cliente logo abaixo — sobre a
      // MESMA página, que é o que o usuário está vendo.
      if (status.length === 1) params.status = status[0];
      if (dataInicio) params.dataInicio = dataInicio;
      if (dataFim) params.dataFim = dataFim;
      if (f.busca.trim()) params.busca = f.busca.trim();

      const { data } = await api.get<HistoricoAtfResponse>('/cooperado/atf', { params });
      if (versao !== versaoRef.current) return;   // outra busca começou depois desta
      const recorte = status.length > 1 ? data.data.filter(i => status.includes(i.status)) : data.data;

      setItens(recorte);
      // Com o recorte local a contagem do servidor mentiria: ela conta a janela
      // inteira, não o subconjunto que ficou na tela.
      setTotal(status.length > 1 ? recorte.length : data.total);
      setAtualizadoEm(new Date());
      sinaisRef.current = {
        emAndamento: recorte.some(i => atfStatusInfo(i.status, i.chPlc).emAndamento),
        revalidar: recorte.some(i => atfStatusInfo(i.status, i.chPlc).revalidar),
      };
    } catch {
      if (versao !== versaoRef.current) return;
      setErro('Não foi possível carregar suas ATFs. Verifique a conexão e tente novamente.');
      sinaisRef.current = { emAndamento: false, revalidar: false };
    } finally {
      if (versao === versaoRef.current) {
        setCarregando(false);
        setAtualizando(false);
      }
    }
  }, []);

  /** Reagenda a partir dos sinais em ref — nunca do array de itens. */
  const agendar = useCallback(() => {
    if (timerRef.current) clearTimeout(timerRef.current);
    const { emAndamento, revalidar } = sinaisRef.current;
    if (!emAndamento && !revalidar) return;

    if (rodadasRef.current >= TETO_RODADAS) {
      setTetoAtingido(true);
      return;
    }

    timerRef.current = setTimeout(async () => {
      rodadasRef.current += 1;
      setRodada(rodadasRef.current);
      await buscar(filtrosRef.current);
      agendar();
    }, emAndamento ? INTERVALO_RAPIDO_MS : INTERVALO_LENTO_MS);
  }, [buscar]);

  useEffect(() => {
    let vivo = true;
    (async () => {
      await buscar(filtros, { manual: true });
      if (vivo) agendar();
    })();
    return () => {
      vivo = false;
      if (timerRef.current) clearTimeout(timerRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filtros.status, filtros.periodo, filtros.busca]);

  const atualizarManual = useCallback(async () => {
    await buscar(filtrosRef.current, { manual: true });
    agendar();
  }, [buscar, agendar]);

  const definirFiltro = useCallback((parcial: Partial<FiltrosAcompanhamento>) => {
    setFiltros(f => ({ ...f, ...parcial }));
  }, []);

  return {
    itens, total, carregando, atualizando, atualizadoEm, erro,
    filtros, definirFiltro, tetoAtingido, rodada, totalRodadas: TETO_RODADAS,
    atualizarManual,
    /**
     * Há rodada agendada de verdade.
     *
     * A tela dizia "atualizando a cada 10 s" sempre que houvesse itens — mesmo
     * com todas as ATF já emitidas, quando nenhum timer chega a ser criado.
     */
    acompanhando: (sinaisRef.current.emAndamento || sinaisRef.current.revalidar) && !tetoAtingido,
  };
}

export function useMotivosCancelamento() {
  const [motivos, setMotivos] = useState<MotivoCancelamento[]>([]);

  useEffect(() => {
    api.get<MotivoCancelamento[]>('/cooperado/atf/motivos-cancelamento')
      .then(res => setMotivos(res.data))
      .catch(() => {
        // Fallback local — mesmo padrão do app: a lista não é dependência dura.
        setMotivos([
          { codigo: 0, rotulo: 'Contratante cancelou a viagem' },
          { codigo: 1, rotulo: 'Emissão em duplicidade' },
          { codigo: 2, rotulo: 'Emissão equivocada ou com erros' },
          { codigo: 3, rotulo: 'Viagem adiada para outra data/horário' },
          { codigo: 4, rotulo: 'Viagem antecipada para outra data/horário' },
          { codigo: 5, rotulo: 'Viagem não realizada' },
        ]);
      });
  }, []);

  return motivos;
}
